import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * QuickAdd context: sayfalar kendi add aksiyonlarını kayıt eder,
 * header’daki "+" menüsü son kayıtlı handler’ları kullanır.
 */
const QuickAddContext = createContext({
  actions: {},
  registerActions: () => undefined,
  unregister: () => undefined,
});

const sanitizeActions = (actions = {}) => {
  const clean = {};
  Object.entries(actions).forEach(([key, fn]) => {
    if (typeof fn === "function") clean[key] = fn;
  });
  return clean;
};

export const QuickAddProvider = ({ children }) => {
  const [registrations, setRegistrations] = useState([]); // { token, actions }
  const tokenRef = useRef(0);

  const registerActions = useCallback((actions = {}) => {
    const cleaned = sanitizeActions(actions);
    if (!Object.keys(cleaned).length) return null;

    const token = ++tokenRef.current;
    setRegistrations((prev) => [...prev, { token, actions: cleaned }]);
    return token;
  }, []);

  const unregister = useCallback((token) => {
    if (!token) return;
    setRegistrations((prev) => prev.filter((r) => r.token !== token));
  }, []);

  const actions = useMemo(() => {
    const merged = {};
    registrations.forEach((r) => {
      Object.entries(r.actions || {}).forEach(([key, fn]) => {
        merged[key] = fn; // son kayıt kazansın
      });
    });
    return merged;
  }, [registrations]);

  const value = useMemo(
    () => ({ actions, registerActions, unregister }),
    [actions, registerActions, unregister]
  );

  return <QuickAddContext.Provider value={value}>{children}</QuickAddContext.Provider>;
};

export const useQuickAdd = () => useContext(QuickAddContext);

/**
 * Kolay kayıt hook'u. `actions`'ı MOUNT'ta TEK SEFER register eder; en güncel
 * fonksiyonlara ref üzerinden delege eder. Böylece çağıran inline obje/fonksiyon
 * geçse bile (deps GEREKMEDEN) sonsuz döngü imkansızdır — eski sürümde `actions`
 * her render yeni referans olduğu için effect dep'i kararsızdı → "Maximum update
 * depth" (Notes/Todo). Kök çözüm: ref + mount-once proxy (yama değil).
 */
export const useQuickAddRegistrar = (actions = {}) => {
  const { registerActions, unregister } = useQuickAdd();
  const actionsRef = useRef(actions);
  actionsRef.current = actions; // her render en güncel fonksiyonlar

  useEffect(() => {
    const keys = Object.keys(actionsRef.current || {});
    if (!keys.length) return undefined;
    // Stabil proxy: provider çağırınca ref'teki güncel fn'e iletir.
    const proxy = {};
    keys.forEach((k) => {
      proxy[k] = (...args) => actionsRef.current?.[k]?.(...args);
    });
    const token = registerActions(proxy);
    return () => unregister(token);
    // Mount'ta tek sefer — registerActions/unregister provider'da stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default QuickAddProvider;
